import { Mic } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import { shouldSubmitComposerKeyDown } from '@/components/case-partner/components/composerKeyDown';
import { IDLE_TIMEOUT_MS, useDictation } from '@/components/case-partner/hooks/useDictation';
import {
  START_FAILED_MESSAGE,
  useSendToCasePartnerPanel,
} from '@/components/case-partner/hooks/useSendToCasePartnerPanel';
import { ChatInput } from '@/components/ui/chat-input';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import { useWebSearchDefault } from '@/hooks/useWebSearchDefault';
import { cn } from '@/lib/utils';
import { sanitizeUserInput } from '@/src/utils/sanitization';

interface CasePartnerHandoffInputProps {
  matterGuid: string;
  placeholder: string;
  prompts?: string[];
}

/**
 * Embeddable Case Partner box. Enter hands the prompt to a new side-panel chat
 * and stays unattached so the next send can start another chat in parallel.
 */
export const CasePartnerHandoffInput = ({
  matterGuid,
  placeholder,
  prompts = [],
}: CasePartnerHandoffInputProps) => {
  const sendToPanel = useSendToCasePartnerPanel(matterGuid);
  const { isEnabled: webResearchEnabled, settings: webResearchSettings } = useFeatures(FEATURE_FLAGS.WEB_RESEARCH);
  const webSearchAvailable = webResearchEnabled && webResearchSettings.configured !== false;
  const { enabled: webSearchDefault } = useWebSearchDefault();
  const [draft, setDraft] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const dictation = useDictation({
    matterGuid,
    value: draft,
    onChange: setDraft,
    onIdleStop: () =>
      toast(`Dictation stopped after ${Math.round(IDLE_TIMEOUT_MS / 1000)} seconds of silence.`, {
        icon: <Mic className="h-4 w-4 text-slate-500" />,
        style: { whiteSpace: 'nowrap', maxWidth: 'none' },
      }),
  });
  const { error: dictationError, clearError: clearDictationError, stop: stopDictation } = dictation;

  React.useEffect(() => {
    if (!dictationError) return;
    toast.error(dictationError);
    clearDictationError();
  }, [dictationError, clearDictationError]);

  const handleSend = React.useCallback(async (value = draft) => {
    if (isSending) return;
    const sanitized = sanitizeUserInput(value);
    if (!sanitized.trim()) return;

    setIsSending(true);
    stopDictation({ discard: true });
    try {
      const result = await sendToPanel(sanitized, {
        webSearch: webSearchAvailable && webSearchDefault,
      });
      if (result.sent) {
        setDraft('');
        return;
      }
      toast.error(result.error || START_FAILED_MESSAGE);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || START_FAILED_MESSAGE);
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  }, [draft, isSending, sendToPanel, stopDictation, webSearchAvailable, webSearchDefault]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!shouldSubmitComposerKeyDown(event)) {
      return;
    }
    event.preventDefault();
    void handleSend();
  };

  return (
    <>
      <ChatInput
        ref={inputRef}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onClickButton={async () => {
          await handleSend();
        }}
        actionDisabled={isSending}
        onDictateClick={dictation.toggle}
        onDictatePrewarm={dictation.prewarm}
        isDictating={dictation.isRecording}
        isDictationConnecting={dictation.isConnecting}
        dictationSupported={dictation.isSupported}
        placeholder={placeholder}
      />
      {prompts.length > 0 && (
        <div className="mt-6 flex flex-wrap justify-center gap-x-2.5 gap-y-3">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className={cn(
                'rounded-full border border-slate-200 bg-slate-50 px-3.5 py-2',
                'text-xs font-medium text-slate-600 transition-colors',
                'hover:border-slate-300 hover:bg-white hover:text-slate-800',
                isSending && 'pointer-events-none opacity-50',
              )}
              disabled={isSending}
              onClick={() => {
                setDraft(prompt);
                inputRef.current?.focus();
              }}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}
    </>
  );
};
